import { c as _c } from "react/compiler-runtime";
import { Grid, Stack, Text } from '@chakra-ui/react';
import type { ScoProduct } from '@src/graphql-types';
import type { ReactNode } from 'react';
import { PaywallHero } from './PaywallHero';
import { PaywallProductCard, type PaywallCardAppearance } from './PaywallProductCard';
import { orderProductsForIntent, resolvePaywallIntent, type PaywallIntentId } from './intent';
import { includedPlanFor } from './presentation';
export type PaywallProductGridProps = {
  products: ScoProduct[];
  cardAppearance?: PaywallCardAppearance;
  intent?: PaywallIntentId;
  tokens?: Record<string, string>;
  ctaLabels?: Record<string, string>;
  currentProductIds?: string[];
  /** Billing term already paid, keyed by productId. See `productOfferState`. */
  currentTermByProductId?: Record<string, string | null>;
  eligibleProductIds?: string[];
  showPlanSummary?: boolean;
  /** Render the intent hero above the cards. Off when the page supplies its own heading. */
  showHero?: boolean;
  /** Caller-verified reassurance under each CTA, such as “Cancel anytime.” */
  assurance?: string;
  /** Offer controls (billing term, promotion code) between the hero and the cards they price. */
  controls?: ReactNode;
  /** Under the cards: offer controls only some visitors need, such as a promotion code field. */
  cardsFooter?: ReactNode;
  /** `center` centers the cards under a centered section heading (landing pages). */
  align?: 'start' | 'center';
  /** `given` keeps the caller’s order (Free, then paid). `recommended` leads with the intent’s plan. */
  productOrder?: 'recommended' | 'given';
  onContinue?: (product: ScoProduct) => void;
};

/** Plans stay a readable width side by side and wrap to one column before they get cramped. */
const CARD_MIN_WIDTH = '18rem';
const CARD_MAX_WIDTH = 32;
export const PaywallProductGrid = t0 => {
  const $ = _c(55);
  const {
    align: t1,
    assurance,
    cardAppearance,
    cardsFooter,
    controls,
    ctaLabels,
    currentProductIds,
    currentTermByProductId,
    eligibleProductIds,
    intent,
    onContinue,
    productOrder: t2,
    products,
    showHero: t3,
    showPlanSummary,
    tokens
  } = t0;
  const align = t1 === undefined ? "start" : t1;
  const productOrder = t2 === undefined ? "recommended" : t2;
  const showHero = t3 === undefined ? true : t3;
  let T0;
  let T1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== align || $[1] !== assurance || $[2] !== cardAppearance || $[3] !== controls || $[4] !== ctaLabels || $[5] !== currentProductIds || $[6] !== currentTermByProductId || $[7] !== eligibleProductIds || $[8] !== intent || $[9] !== onContinue || $[10] !== productOrder || $[11] !== products || $[12] !== showHero || $[13] !== showPlanSummary || $[14] !== tokens) {
    t15 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const resolved = resolvePaywallIntent(intent, tokens);
      const ordered = productOrder === "given" ? products : orderProductsForIntent(products, resolved);
      if (ordered.length === 0) {
        let t16;
        if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
          t16 = <Text color="fg.muted" textStyle="sm">Plans aren’t available right now.</Text>;
          $[29] = t16;
        } else {
          t16 = $[29];
        }
        t15 = t16;
        break bb0;
      }
      T1 = Stack;
      if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
        t12 = {
          base: 6,
          md: 8
        };
        $[30] = t12;
      } else {
        t12 = $[30];
      }
      if ($[31] !== intent || $[32] !== products || $[33] !== showHero || $[34] !== showPlanSummary || $[35] !== tokens) {
        t13 = showHero ? <PaywallHero intent={intent} products={products} showPlanSummary={showPlanSummary} tokens={tokens} /> : null;
        $[31] = intent;
        $[32] = products;
        $[33] = showHero;
        $[34] = showPlanSummary;
        $[35] = tokens;
        $[36] = t13;
      } else {
        t13 = $[36];
      }
      t14 = controls;
      T0 = Grid;
      t4 = 5;
      t5 = `${ordered.length * CARD_MAX_WIDTH}rem`;
      t6 = align === "center" ? "auto" : undefined;
      t7 = 3;
      if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = {
          base: 8,
          md: 5
        };
        $[37] = t8;
      } else {
        t8 = $[37];
      }
      t9 = `repeat(auto-fit, minmax(min(100%, ${CARD_MIN_WIDTH}), 1fr))`;
      t10 = "full";
      t11 = ordered.map(product => <PaywallProductCard key={product.productId || product.id} appearance={cardAppearance} assurance={assurance} ctaLabel={product.productId ? ctaLabels?.[product.productId] : undefined} currentProductIds={currentProductIds} currentTermByProductId={currentTermByProductId} eligibleProductIds={eligibleProductIds} includedPlan={includedPlanFor(product, products, resolved)} intent={intent} product={product} tokens={tokens} onContinue={onContinue} />);
    }
    $[0] = align;
    $[1] = assurance;
    $[2] = cardAppearance;
    $[3] = controls;
    $[4] = ctaLabels;
    $[5] = currentProductIds;
    $[6] = currentTermByProductId;
    $[7] = eligibleProductIds;
    $[8] = intent;
    $[9] = onContinue;
    $[10] = productOrder;
    $[11] = products;
    $[12] = showHero;
    $[13] = showPlanSummary;
    $[14] = tokens;
    $[15] = T0;
    $[16] = T1;
    $[17] = t10;
    $[18] = t11;
    $[19] = t12;
    $[20] = t13;
    $[21] = t14;
    $[22] = t15;
    $[23] = t4;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
  } else {
    T0 = $[15];
    T1 = $[16];
    t10 = $[17];
    t11 = $[18];
    t12 = $[19];
    t13 = $[20];
    t14 = $[21];
    t15 = $[22];
    t4 = $[23];
    t5 = $[24];
    t6 = $[25];
    t7 = $[26];
    t8 = $[27];
    t9 = $[28];
  }
  if (t15 !== Symbol.for("react.early_return_sentinel")) {
    return t15;
  }
  let t16;
  if ($[38] !== T0 || $[39] !== t10 || $[40] !== t11 || $[41] !== t4 || $[42] !== t5 || $[43] !== t6 || $[44] !== t7 || $[45] !== t8 || $[46] !== t9) {
    t16 = <T0 columnGap={t4} maxW={t5} mx={t6} pt={t7} rowGap={t8} templateColumns={t9} w={t10}>{t11}</T0>;
    $[38] = T0;
    $[39] = t10;
    $[40] = t11;
    $[41] = t4;
    $[42] = t5;
    $[43] = t6;
    $[44] = t7;
    $[45] = t8;
    $[46] = t9;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  let t17;
  if ($[48] !== T1 || $[49] !== cardsFooter || $[50] !== t12 || $[51] !== t13 || $[52] !== t14 || $[53] !== t16) {
    t17 = <T1 gap={t12}>{t13}{t14}{t16}{cardsFooter}</T1>;
    $[48] = T1;
    $[49] = cardsFooter;
    $[50] = t12;
    $[51] = t13;
    $[52] = t14;
    $[53] = t16;
    $[54] = t17;
  } else {
    t17 = $[54];
  }
  return t17;
};